// Timezone helpers for the countdown timer.
// We use the IANA TZ database (via Intl.DateTimeFormat) to map a wall-clock
// "HH:MM today in zone X" to a UTC epoch ms, which the timer can count down to.

export const TIMEZONE_OPTIONS = [
  { id: 'local', label: 'Local (browser)', iana: null },
  { id: 'America/New_York', label: 'Eastern (New York)', iana: 'America/New_York', short: 'ET' },
  { id: 'America/Chicago', label: 'Central (Chicago)', iana: 'America/Chicago', short: 'CT' },
  { id: 'America/Denver', label: 'Mountain (Denver)', iana: 'America/Denver', short: 'MT' },
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)', iana: 'America/Los_Angeles', short: 'PT' },
  { id: 'America/Anchorage', label: 'Alaska (Anchorage)', iana: 'America/Anchorage', short: 'AKT' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)', iana: 'Pacific/Honolulu', short: 'HST' },
  { id: 'UTC', label: 'UTC', iana: 'UTC', short: 'UTC' },
  { id: 'Europe/London', label: 'London', iana: 'Europe/London', short: 'GMT/BST' },
  { id: 'Europe/Paris', label: 'Paris / Berlin', iana: 'Europe/Paris', short: 'CET' },
  { id: 'Europe/Athens', label: 'Athens / Helsinki', iana: 'Europe/Athens', short: 'EET' },
  { id: 'Asia/Dubai', label: 'Dubai', iana: 'Asia/Dubai', short: 'GST' },
  { id: 'Asia/Kolkata', label: 'Mumbai / Delhi', iana: 'Asia/Kolkata', short: 'IST' },
  { id: 'Asia/Singapore', label: 'Singapore', iana: 'Asia/Singapore', short: 'SGT' },
  { id: 'Asia/Tokyo', label: 'Tokyo', iana: 'Asia/Tokyo', short: 'JST' },
  { id: 'Australia/Sydney', label: 'Sydney', iana: 'Australia/Sydney', short: 'AEST' },
];

export const getTimezoneOption = (id) =>
  TIMEZONE_OPTIONS.find((tz) => tz.id === id) || TIMEZONE_OPTIONS[0];

// Returns the offset (ms) between the given timezone and UTC at the given Date.
// Positive when the timezone is ahead of UTC (e.g. Tokyo = +9h = +32400000).
const getZoneOffsetMs = (date, ianaTz) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTz,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0; // Intl returns "24" for midnight in some envs
  const asUTC = Date.UTC(
    Number(get('year')),
    Number(get('month')) - 1,
    Number(get('day')),
    hour,
    Number(get('minute')),
    Number(get('second')),
  );
  return asUTC - date.getTime();
};

// Returns the today's Y/M/D in the given timezone.
const getZoneDateParts = (date, ianaTz) => {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const [y, mo, d] = dtf.format(date).split('-').map(Number);
  return { y, mo, d };
};

/**
 * Compute the UTC epoch (ms) for the next occurrence of "HH:MM" in the given timezone.
 * If the target time has already passed today in that zone, returns tomorrow's instant.
 */
export const nextTargetEpoch = (hour, minute, tzId) => {
  const tz = getTimezoneOption(tzId);
  const now = new Date();
  if (!tz.iana) {
    // Local browser timezone path — straightforward.
    const target = new Date();
    target.setHours(hour, minute, 0, 0);
    if (target.getTime() <= now.getTime()) {
      target.setDate(target.getDate() + 1);
    }
    return target.getTime();
  }
  const zoneOffset = getZoneOffsetMs(now, tz.iana);
  const { y, mo, d } = getZoneDateParts(now, tz.iana);
  let target = Date.UTC(y, mo - 1, d, hour, minute, 0) - zoneOffset;
  if (target <= now.getTime()) {
    target += 24 * 60 * 60 * 1000;
  }
  return target;
};

/**
 * Format "HH:MM" in a target timezone as a human-readable "h:mm AM/PM" string.
 * E.g. for tz=Tokyo, "09:33" -> "9:33 AM JST".
 */
export const formatTargetInZone = (hour, minute, tzId) => {
  const tz = getTimezoneOption(tzId);
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  if (!tz.iana || tz.id === 'local') return timeStr;
  return `${timeStr} ${tz.short || tz.label}`;
};

/**
 * Returns the current wall-clock time in the target timezone formatted as h:mm AM/PM.
 * Used so the presenter knows what "now" looks like in the chosen zone.
 */
export const currentTimeInZone = (tzId) => {
  const tz = getTimezoneOption(tzId);
  const now = new Date();
  const options = { hour: 'numeric', minute: '2-digit', hour12: true };
  if (tz.iana) options.timeZone = tz.iana;
  return now.toLocaleTimeString([], options);
};
